import { Bell, Loader2, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { NotificationPreference } from '../types/notification';

const ROWS = [
  { label: 'Task Assigned', prefix: 'task_assigned', desc: 'When a task is assigned to you' },
  { label: 'Status Changed', prefix: 'task_status', desc: 'When a task status is updated' },
  { label: 'Comments', prefix: 'task_comment', desc: 'When someone comments on your task' },
  { label: 'Mentions', prefix: 'task_mention', desc: 'When you are mentioned in a comment' },
] as const;

const CHANNELS = ['email', 'in_app', 'push'] as const;
const CHANNEL_LABELS: Record<string, string> = { email: 'Email', in_app: 'In-App', push: 'Push' };

const NotificationPreferences: React.FC = () => {
  const [prefs, setPrefs] = useState<NotificationPreference | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const headers = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/notifications/preferences', { headers: headers() });
        if (res.ok) {
          const { data } = await res.json();
          setPrefs(data);
        }
      } catch (err) {
        console.error('Failed to load preferences:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (key: string) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [key]: !(prefs as any)[key] } as NotificationPreference);
    setDirty(true);
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const body: Record<string, boolean> = {};
      for (const row of ROWS) {
        for (const ch of CHANNELS) {
          const key = `${row.prefix}_${ch}`;
          body[key] = (prefs as any)[key];
        }
      }
      const res = await fetch('/api/notifications/preferences', {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (res.ok) setDirty(false);
    } catch (err) {
      console.error('Failed to save preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} currentPage="settings" />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-gray-600" />
              <CardTitle>Notification Preferences</CardTitle>
            </div>
            <CardDescription>Choose how you want to be notified for different events.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : prefs ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 pr-4 font-medium text-gray-700">Event</th>
                        {CHANNELS.map(ch => (
                          <th key={ch} className="text-center py-3 px-4 font-medium text-gray-700">
                            {CHANNEL_LABELS[ch]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ROWS.map(row => (
                        <tr key={row.prefix} className="border-b last:border-0">
                          <td className="py-4 pr-4">
                            <p className="font-medium text-gray-900">{row.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{row.desc}</p>
                          </td>
                          {CHANNELS.map(ch => {
                            const key = `${row.prefix}_${ch}`;
                            return (
                              <td key={ch} className="text-center py-4 px-4">
                                <Checkbox
                                  checked={(prefs as any)[key]}
                                  onCheckedChange={() => toggle(key)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-6">
                  <Button onClick={save} disabled={!dirty || saving}>
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Preferences
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500 py-8 text-center">Failed to load preferences.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NotificationPreferences;
